"use client";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCopilotChat } from "@zapier/copilot-ui/client";
import { Icon, IconName } from "@zapier/zinnia-icons";
import { ActionChip, ActionChipIcon, Button } from "@zapier/design-system";
import { SupportChipConfig } from "./types";
import { createLogger } from "../../utils/logger";

const logger = createLogger("SupportChips");

const containerStyles = {
  "copilot-input": css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--zds-space-8);
    margin-top: var(--zds-space-12);
  `,
  default: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--zds-space-8);
    padding: var(--zds-space-8) var(--zds-space-12);
    background: transparent;
  `,
};

/**
 * Use as a Ghost Button with custom link-weight typography to give
 * a style similar to a link.
 */
const LinkButton = styled(Button)`
  font-weight: 400;
  padding-left: 4px;
  padding-right: 4px;
`;

interface SupportChipsConfigurableProps {
  chips: SupportChipConfig[];
  /** Current rendering surface — drives layout and filters chips by their surfaces array */
  surface?: string;
  /** Compact-view callback: transitions to fullscreen before appending a message chip */
  onEngageCopilot?: (query: string) => void;
  onResumeChat?: () => void;
  onResumeConversation?: () => void;
  onNewConversation?: () => void;
}

export const SupportChipsConfigurable = ({
  chips,
  surface,
  onEngageCopilot,
  onResumeChat,
  onResumeConversation,
  onNewConversation,
}: SupportChipsConfigurableProps) => {
  const { append } = useCopilotChat();

  const visibleChips = chips.filter(
    (c) => !c.surfaces || !surface || c.surfaces.includes(surface),
  );

  const handleChipClick = (chip: SupportChipConfig) => {
    logger.log("🖱️ Chip clicked:", {
      chipId: chip.id,
      chipAction: chip.action,
    });

    if (chip.action === "resumeChat") {
      onResumeChat?.();
      return;
    }
    if (chip.action === "resumeConversation") {
      onResumeConversation?.();
      return;
    }
    if (chip.action === "newConversation") {
      onNewConversation?.();
      return;
    }

    if (!chip.message) return;

    if (onEngageCopilot) {
      onEngageCopilot("");
      setTimeout(() => append({ role: "user", content: chip.message! }), 100);
    } else {
      append({ role: "user", content: chip.message });
    }
  };

  if (visibleChips.length === 0) return null;

  const containerStyle =
    surface === "copilot-input"
      ? containerStyles["copilot-input"]
      : containerStyles.default;

  return (
    <div css={containerStyle}>
      {visibleChips.map((chip) => {
        if (chip.variant?.startsWith("Button-")) {
          const buttonVariant = chip.variant.slice("Button-".length) as
            | "primary"
            | "secondary"
            | "tertiary";
          return (
            <Button
              key={chip.id}
              variant={buttonVariant}
              size="small"
              onPress={() => handleChipClick(chip)}
            >
              {chip.icon && <Icon name={chip.icon as IconName} size={16} />}
              {chip.label}
            </Button>
          );
        }

        if (surface === "copilot-input") {
          return (
            <LinkButton
              key={chip.id}
              variant="ghost"
              size="small"
              onPress={() => handleChipClick(chip)}
            >
              {chip.icon && <Icon name={chip.icon as IconName} size={16} />}
              {chip.label}
            </LinkButton>
          );
        }

        const actionChipVariant =
          (chip.variant?.startsWith("ActionChip-") &&
            (chip.variant.slice("ActionChip-".length) as
              | "primary"
              | "ghost")) ||
          "ghost";
        return (
          <ActionChip
            key={chip.id}
            variant={actionChipVariant}
            onPress={() => handleChipClick(chip)}
            size="small"
          >
            {chip.icon && <ActionChipIcon name={chip.icon as any} />}
            {chip.label}
          </ActionChip>
        );
      })}
    </div>
  );
};
